import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useDraggable } from "@dnd-kit/core";
import {
  Archive,
  Circle,
  Copy,
  GripVertical,
  Loader2,
  Minus,
  Pencil,
  Play,
  RotateCcw,
  Sparkles,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import { ActivityCell } from "@/components/WorkflowActivity";
import { useFolderSummaryByWorkflows } from "@/components/FolderCountBadge";
import FolderChipList from "@/components/FolderChipList";
import { workflowRef } from "@/lib/workflowNumber";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function WorkflowTable({
  items,
  onEdit,
  onDuplicate,
  onArchive,
  onUnarchive,
  onRun,
  // R2-Phase-4 — optional multi-select for the bulk folder bar.  Pass
  // `selectedIds` as a Set to enable; omit (or pass undefined) to hide
  // the checkbox column entirely (back-compat for any other caller).
  selectedIds,
  onToggleSelected,
  onTogglePageSelection,
  // Phase FOLDER-UX (FU.3) — when truthy, render the leading drag-
  // handle column and wire each handle to @dnd-kit's useDraggable.
  // The DndContext lives in the parent page (WorkflowLibraryPage).
  dndEnabled = false,
}) {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("workflow.write");
  const canRun = hasPermission("workflow.run");
  const selectionEnabled = selectedIds instanceof Set;

  // Batch-fetch the 7-day activity for all visible workflows.
  const ids = useMemo(
    () => (items || []).map((w) => w.workflow_id),
    [items],
  );
  const idsCsv = ids.join(",");
  const activity = useQuery({
    queryKey: ["activity-summary", idsCsv, 7],
    queryFn: async () =>
      (
        await api.get("/workflows/activity-summary", {
          params: { ids: idsCsv, days: 7 },
        })
      ).data,
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
  const activityByWf = useMemo(() => {
    const m = {};
    (activity.data || []).forEach((row) => {
      m[row.workflow_id] = row;
    });
    return m;
  }, [activity.data]);

  // R2-Phase-4 — batch folder-membership summary, same shape as runs.
  const folderSummaryQuery = useFolderSummaryByWorkflows(ids);
  const folderSummary = folderSummaryQuery.data || {};
  const allPageSelected =
    selectionEnabled && ids.length > 0 && ids.every((id) => selectedIds.has(id));
  const somePageSelected =
    selectionEnabled &&
    !allPageSelected &&
    ids.some((id) => selectedIds.has(id));
  if (!items || items.length === 0) {
    return (
      <div
        data-testid="workflows-empty"
        className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center"
      >
        <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-indigo-50 text-indigo-600">
          <Play size={18} />
        </div>
        <p className="text-sm font-medium text-slate-700">No workflows here yet.</p>
        <p className="mt-1 text-xs text-slate-500">
          Click <span className="font-medium">New workflow</span> in the top-right to create one.
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table data-testid="workflow-table">
          <TableHeader className="bg-slate-50">
            <TableRow>
              {dndEnabled && (
                <TableHead
                  className="w-7 px-1 text-xs uppercase tracking-wider text-slate-500"
                  aria-label="Drag to folder"
                />
              )}
              {selectionEnabled && (
                <TableHead className="w-9 px-3 text-xs uppercase tracking-wider text-slate-500">
                  <Checkbox
                    checked={allPageSelected ? true : somePageSelected ? "indeterminate" : false}
                    onCheckedChange={() => onTogglePageSelection?.()}
                    aria-label="Select all workflows on this page"
                    data-testid="workflows-select-all"
                  />
                </TableHead>
              )}
              <TableHead className="w-16 text-xs uppercase tracking-wider text-slate-500">
                #
              </TableHead>
              <TableHead className="w-44 text-xs uppercase tracking-wider text-slate-500">
                Folders
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500">
                Name
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500">
                Tags
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500">
                Auto-pipeline
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500">
                Version
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500">
                Status
              </TableHead>
              <TableHead className="w-12 text-center text-xs uppercase tracking-wider text-slate-500">
                Video
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500">
                Activity (7d)
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500">
                Updated
              </TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((wf) => (
              <TableRow
                key={wf.workflow_id}
                data-testid={`workflow-row-${wf.slug}`}
                className="hover:bg-slate-50/60"
              >
                {dndEnabled && (
                  <TableCell className="w-7 px-1">
                    <WorkflowDragHandle
                      workflow={wf}
                      selectedIds={selectedIds}
                    />
                  </TableCell>
                )}
                {selectionEnabled && (
                  <TableCell className="px-3">
                    <Checkbox
                      checked={selectedIds.has(wf.workflow_id)}
                      onCheckedChange={() => onToggleSelected?.(wf.workflow_id)}
                      aria-label={`Select workflow ${wf.name}`}
                      data-testid={`workflows-select-${wf.slug}`}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <span
                    className="font-mono text-sm text-slate-600"
                    data-testid={`workflow-number-${wf.slug}`}
                  >
                    {workflowRef(wf.workflow_number)}
                  </span>
                </TableCell>
                <TableCell>
                  <FolderChipList
                    workflowId={wf.workflow_id}
                    workflowSlug={wf.slug}
                    workflowName={wf.name}
                    summary={folderSummary[wf.workflow_id]}
                    onRemoved={() => folderSummaryQuery.refetch()}
                  />
                </TableCell>
                <TableCell>
                  <Link
                    to={`/workflows/${wf.workflow_id}`}
                    className="font-medium text-slate-900 hover:text-indigo-700"
                    data-testid={`workflow-name-${wf.slug}`}
                  >
                    {wf.name}
                  </Link>
                  <div className="text-[11px] text-slate-400">
                    {wf.step_count} step{wf.step_count === 1 ? "" : "s"} · slug:{" "}
                    <code className="font-mono">{wf.slug}</code>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {wf.tags?.length ? (
                      wf.tags.map((t) => (
                        <Badge
                          key={t}
                          variant="outline"
                          className="rounded-full border-indigo-200 bg-indigo-50 text-[10px] font-medium text-indigo-700"
                        >
                          {t}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <AutoPipelinePill enabled={wf.auto_pipeline !== false} slug={wf.slug} />
                </TableCell>
                <TableCell>
                  <span className="font-mono text-sm text-slate-700">v{wf.current_version}</span>
                </TableCell>
                <TableCell>
                  <StatusPill status={wf.status} />
                </TableCell>
                <TableCell className="text-center">
                  <LatestVideoStatusBadge
                    status={wf.latest_video_status}
                    slug={wf.slug}
                  />
                </TableCell>
                <TableCell>
                  <ActivityCell
                    workflowId={wf.workflow_id}
                    data={activityByWf[wf.workflow_id]}
                    isLoading={activity.isLoading}
                  />
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  {formatDate(wf.updated_at)}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={!canRun || wf.status !== "active"}
                            onClick={() => canRun && wf.status === "active" && onRun?.(wf)}
                            data-testid={`workflow-run-${wf.slug}`}
                            aria-label="Generate Video (start recording)"
                            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 disabled:text-slate-400"
                          >
                            <Circle size={11} fill="currentColor" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {!canRun
                          ? "Requires presenter role"
                          : wf.status !== "active"
                          ? "Archived workflows can't be run"
                          : "Generate Video"}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={!canWrite}
                            onClick={() => canWrite && onEdit?.(wf)}
                            data-testid={`workflow-edit-${wf.slug}`}
                            aria-label="Edit workflow"
                          >
                            <Pencil size={15} />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{canWrite ? "Edit" : "Requires builder role"}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={!canWrite}
                            onClick={() => canWrite && onDuplicate?.(wf)}
                            data-testid={`workflow-duplicate-${wf.slug}`}
                            aria-label="Duplicate workflow"
                          >
                            <Copy size={15} />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{canWrite ? "Duplicate" : "Requires builder role"}</TooltipContent>
                    </Tooltip>
                    {wf.status === "active" ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={!canWrite}
                              onClick={() => canWrite && onArchive?.(wf)}
                              data-testid={`workflow-archive-${wf.slug}`}
                              aria-label="Archive workflow"
                            >
                              <Archive size={15} />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{canWrite ? "Archive" : "Requires builder role"}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={!canWrite}
                              onClick={() => canWrite && onUnarchive?.(wf)}
                              data-testid={`workflow-unarchive-${wf.slug}`}
                              aria-label="Unarchive workflow"
                            >
                              <RotateCcw size={15} />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{canWrite ? "Unarchive" : "Requires builder role"}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}

function AutoPipelinePill({ enabled, slug }) {
  return (
    <span
      data-testid={`auto-pipeline-pill-${slug}`}
      title={
        enabled
          ? "After this workflow runs, narration + voiceover + final MP4 are auto-generated."
          : "Auto-generation pipeline is OFF. You'll need to press the manual buttons after each run."
      }
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
        enabled
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-500",
      ].join(" ")}
    >
      <Sparkles
        size={11}
        className={enabled ? "text-emerald-600" : "text-slate-400"}
      />
      {enabled ? "ON" : "OFF"}
    </span>
  );
}

function LatestVideoStatusBadge({ status, slug }) {
  // R3 UI close-out — compact 24x24 icon badge.  Tooltip wraps the icon
  // and carries the human-readable explanation; the icon itself stays
  // small enough to slot between Status and Activity without forcing
  // any column to wrap on a 1280px display.
  const map = {
    ready: {
      Icon: Play,
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      label: "Latest video ready",
      spin: false,
    },
    failed: {
      Icon: XCircle,
      cls: "bg-rose-50 text-rose-700 border-rose-200",
      label: "Latest generation failed",
      spin: false,
    },
    in_progress: {
      Icon: Loader2,
      cls: "bg-indigo-50 text-indigo-700 border-indigo-200",
      label: "Currently generating…",
      spin: true,
    },
    none: {
      Icon: Minus,
      cls: "bg-slate-50 text-slate-400 border-slate-200",
      label: "No video yet",
      spin: false,
    },
  };
  const cfg = map[status] || map.none;
  const { Icon } = cfg;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid={`workflow-video-status-${slug}`}
          data-video-status={status || "none"}
          className={[
            "inline-flex h-6 w-6 items-center justify-center rounded-full border",
            cfg.cls,
          ].join(" ")}
          aria-label={cfg.label}
        >
          <Icon size={12} className={cfg.spin ? "animate-spin" : ""} />
        </span>
      </TooltipTrigger>
      <TooltipContent>{cfg.label}</TooltipContent>
    </Tooltip>
  );
}

function StatusPill({ status }) {
  const isArchived = status === "archived";
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
        isArchived
          ? "bg-slate-100 text-slate-600"
          : "bg-emerald-50 text-emerald-700",
      ].join(" ")}
    >
      <span
        className={[
          "h-1.5 w-1.5 rounded-full",
          isArchived ? "bg-slate-400" : "bg-emerald-500",
        ].join(" ")}
      />
      {isArchived ? "Archived" : "Active"}
    </span>
  );
}


/**
 * Phase FOLDER-UX (FU.3) — leading drag-handle cell.
 *
 * The handle is the ONLY drag-activator on each row — the rest of the
 * row keeps its normal click + checkbox semantics.  This deliberately
 * narrow target also keeps mobile scrolling responsive (full-row drag
 * traps would fight the touch scroll).
 *
 * When the user has multi-selected rows AND grabs a selected row's
 * handle, the drag payload carries the *Set* of selected workflow ids
 * instead of just this single id — the parent's onDragEnd then issues
 * one folder-write per workflow.
 */
function WorkflowDragHandle({ workflow, selectedIds }) {
  const selectionEnabled = selectedIds instanceof Set;
  const isSelected = selectionEnabled && selectedIds.has(workflow.workflow_id);
  const bulkIds = isSelected && selectedIds.size > 1
    ? Array.from(selectedIds)
    : [workflow.workflow_id];

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `workflow-handle-${workflow.workflow_id}`,
    data: {
      type: "workflow-handle",
      workflow_id: workflow.workflow_id,
      workflow_name: workflow.name,
      selected_ids: bulkIds,
    },
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={setNodeRef}
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Drag workflow ${workflow.name} to folder`}
          data-testid={`workflow-drag-handle-${workflow.slug}`}
          data-dragging={isDragging ? "true" : "false"}
          className={[
            "grid h-6 w-6 cursor-grab place-items-center rounded text-slate-300 transition-colors",
            "hover:bg-slate-100 hover:text-slate-600 active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400",
            isDragging ? "opacity-30" : "",
          ].join(" ")}
        >
          <GripVertical size={14} strokeWidth={2.25} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" align="center" className="text-[11px]">
        Drag to folder · Shift+drop to move
      </TooltipContent>
    </Tooltip>
  );
}
